// Canvas state + reducer + debounced autosave to the server.
//
// Layer:
//   { id, members: [{segmentId, file, dx, dy, w, h}], transform, groupId }
//
// Group:
//   { id, name, collapsed }   (purely for panel organization; Konva
//   render order is still strictly state.layers order)
//
// Selection:
//   selectedIds: string[]     (layer ids; supports multi-select)
//
// Groups own a contiguous z-range: when a group is created, its members
// are reordered to be contiguous in `state.layers`, ending at the position
// of the highest-z member at creation time. Ungroup just clears `groupId`
// (no z reorder).

import { useCallback, useEffect, useReducer, useRef } from "react";
import { nanoid } from "nanoid";
import { fetchCanvasById, putCanvasById } from "../api.js";

const AUTOSAVE_DEBOUNCE_MS = 350;
const SCHEMA_VERSION = 2;

function emptyState() {
  return {
    schema: SCHEMA_VERSION,
    layers: [],
    groups: [],
    selectedIds: [],
    canvasSize: { w: 1200, h: 720 },
  };
}

function defaultTransform() {
  return { x: 0, y: 0, rotation: 0, scaleX: 1, scaleY: 1, mirrorH: false, mirrorV: false };
}

function sanitizePayload(p) {
  if (!p || typeof p !== "object") return emptyState();
  const layers = Array.isArray(p.layers) ? p.layers : [];
  const cleanLayers = layers
    .filter((l) => l && Array.isArray(l.members) && l.members.length > 0)
    .map((l) => ({
      id: l.id,
      members: l.members.filter(
        (m) => m && Number.isFinite(m.segmentId) && m.file && Number.isFinite(m.w) && Number.isFinite(m.h),
      ),
      transform: { ...defaultTransform(), ...(l.transform || {}) },
      groupId: typeof l.groupId === "string" ? l.groupId : null,
      locked: !!l.locked,
      hidden: !!l.hidden,
    }))
    .filter((l) => l.members.length > 0);

  const validGroupIds = new Set();
  const cleanGroups = Array.isArray(p.groups)
    ? p.groups
        .filter((g) => g && typeof g.id === "string")
        .map((g) => {
          validGroupIds.add(g.id);
          return {
            id: g.id,
            name: typeof g.name === "string" ? g.name : "Group",
            collapsed: !!g.collapsed,
          };
        })
    : [];

  // Drop dangling groupId references; drop empty groups.
  for (const l of cleanLayers) {
    if (l.groupId && !validGroupIds.has(l.groupId)) l.groupId = null;
  }
  const usedGroupIds = new Set(cleanLayers.map((l) => l.groupId).filter(Boolean));
  const finalGroups = cleanGroups.filter((g) => usedGroupIds.has(g.id));

  // Selection: support both old `selectedId` and new `selectedIds` payloads.
  let selectedIds = [];
  if (Array.isArray(p.selectedIds)) {
    selectedIds = p.selectedIds.filter((id) => cleanLayers.some((l) => l.id === id));
  } else if (typeof p.selectedId === "string" && cleanLayers.some((l) => l.id === p.selectedId)) {
    selectedIds = [p.selectedId];
  }

  // canvasSize is the user-specified output rectangle in world units.
  // Migrate legacy `viewport: {stageW, stageH}` payloads.
  let canvasSize = { w: 1200, h: 720 };
  const cs = p.canvasSize;
  if (cs && Number.isFinite(cs.w) && Number.isFinite(cs.h) && cs.w > 0 && cs.h > 0) {
    canvasSize = { w: Math.round(cs.w), h: Math.round(cs.h) };
  } else if (
    p.viewport &&
    Number.isFinite(p.viewport.stageW) &&
    Number.isFinite(p.viewport.stageH) &&
    p.viewport.stageW > 0 &&
    p.viewport.stageH > 0
  ) {
    canvasSize = { w: Math.round(p.viewport.stageW), h: Math.round(p.viewport.stageH) };
  }

  return {
    schema: SCHEMA_VERSION,
    layers: cleanLayers,
    groups: finalGroups,
    selectedIds,
    canvasSize,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "hydrate":
      return sanitizePayload(action.payload);

    case "addLayer": {
      const layer = {
        id: action.layer.id || `lyr_${nanoid(8)}`,
        members: action.layer.members,
        transform: {
          ...defaultTransform(),
          x: action.layer.x ?? 0,
          y: action.layer.y ?? 0,
        },
        groupId: null,
        locked: false,
        hidden: false,
      };
      return {
        ...state,
        layers: [...state.layers, layer],
        selectedIds: [layer.id],
      };
    }

    case "selectLayers":
      return { ...state, selectedIds: dedupe(action.ids || []) };

    case "toggleLayerSelection": {
      const has = state.selectedIds.includes(action.id);
      const ids = has
        ? state.selectedIds.filter((x) => x !== action.id)
        : [...state.selectedIds, action.id];
      return { ...state, selectedIds: ids };
    }

    case "removeLayer": {
      const layers = state.layers.filter((l) => l.id !== action.id);
      const selectedIds = state.selectedIds.filter((id) => id !== action.id);
      // Clean up any groups that became empty.
      const stillUsedGroupIds = new Set(layers.map((l) => l.groupId).filter(Boolean));
      const groups = state.groups.filter((g) => stillUsedGroupIds.has(g.id));
      return { ...state, layers, groups, selectedIds };
    }

    case "removeLayers": {
      const ids = new Set(action.ids);
      const layers = state.layers.filter((l) => !ids.has(l.id));
      const selectedIds = state.selectedIds.filter((id) => !ids.has(id));
      const stillUsedGroupIds = new Set(layers.map((l) => l.groupId).filter(Boolean));
      const groups = state.groups.filter((g) => stillUsedGroupIds.has(g.id));
      return { ...state, layers, groups, selectedIds };
    }

    case "transformLayer": {
      const layers = state.layers.map((l) =>
        l.id === action.id
          ? { ...l, transform: { ...l.transform, ...action.patch } }
          : l,
      );
      return { ...state, layers };
    }

    case "transformLayers": {
      const patches = action.patches || {};
      const layers = state.layers.map((l) =>
        patches[l.id]
          ? { ...l, transform: { ...l.transform, ...patches[l.id] } }
          : l,
      );
      return { ...state, layers };
    }

    case "toggleMirror": {
      const targets = new Set(action.ids && action.ids.length ? action.ids : []);
      if (targets.size === 0) return state;
      const layers = state.layers.map((l) => {
        if (!targets.has(l.id)) return l;
        const t = { ...l.transform };
        if (action.axis === "h") t.mirrorH = !t.mirrorH;
        if (action.axis === "v") t.mirrorV = !t.mirrorV;
        return { ...l, transform: t };
      });
      return { ...state, layers };
    }

    case "reorderLayers": {
      const { fromIdx, toIdx } = action;
      if (fromIdx === toIdx) return state;
      const layers = state.layers.slice();
      const [moved] = layers.splice(fromIdx, 1);
      layers.splice(toIdx, 0, moved);
      return { ...state, layers };
    }

    case "bringToFront": {
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      const moved = state.layers.filter((l) => ids.has(l.id));
      const others = state.layers.filter((l) => !ids.has(l.id));
      return { ...state, layers: [...others, ...moved] };
    }

    case "sendToBack": {
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      const moved = state.layers.filter((l) => ids.has(l.id));
      const others = state.layers.filter((l) => !ids.has(l.id));
      return { ...state, layers: [...moved, ...others] };
    }

    case "duplicateLayers": {
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      const offset = action.offset ?? 24;
      const duplicates = [];
      for (const layer of state.layers) {
        if (!ids.has(layer.id)) continue;
        duplicates.push({
          id: `lyr_${nanoid(8)}`,
          members: layer.members.map((m) => ({ ...m })),
          transform: {
            ...layer.transform,
            x: layer.transform.x + offset,
            y: layer.transform.y + offset,
          },
          // Don't carry groupId — duplicates start ungrouped.
          groupId: null,
          locked: false,
          hidden: false,
        });
      }
      if (duplicates.length === 0) return state;
      return {
        ...state,
        layers: [...state.layers, ...duplicates],
        selectedIds: duplicates.map((d) => d.id),
      };
    }

    case "duplicateLayersInPlace": {
      // Used by Alt+drag: insert a copy of each selected layer at the
      // same transform, immediately BELOW the original in z-order, and
      // KEEP the originals selected so the in-progress drag continues
      // moving them. The stationary duplicate is what the user "leaves
      // behind" at the source position.
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      const out = [];
      let inserted = 0;
      for (const layer of state.layers) {
        if (ids.has(layer.id)) {
          out.push({
            id: `lyr_${nanoid(8)}`,
            members: layer.members.map((m) => ({ ...m })),
            transform: { ...layer.transform },
            groupId: null,
            locked: false,
            hidden: false,
          });
          inserted++;
        }
        out.push(layer);
      }
      if (inserted === 0) return state;
      return { ...state, layers: out };
    }

    case "setLayerLocked": {
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      // If `locked` is provided, set all to that value. Otherwise toggle:
      // if any are unlocked, lock all; otherwise unlock all.
      let newValue;
      if (typeof action.locked === "boolean") {
        newValue = action.locked;
      } else {
        const targets = state.layers.filter((l) => ids.has(l.id));
        newValue = targets.some((l) => !l.locked);
      }
      return {
        ...state,
        layers: state.layers.map((l) =>
          ids.has(l.id) ? { ...l, locked: newValue } : l,
        ),
      };
    }

    case "setLayerHidden": {
      const ids = new Set(action.ids || []);
      if (ids.size === 0) return state;
      let newValue;
      if (typeof action.hidden === "boolean") {
        newValue = action.hidden;
      } else {
        const targets = state.layers.filter((l) => ids.has(l.id));
        newValue = targets.some((l) => !l.hidden);
      }
      return {
        ...state,
        layers: state.layers.map((l) =>
          ids.has(l.id) ? { ...l, hidden: newValue } : l,
        ),
      };
    }

    case "recomposeLayer": {
      const { id, added } = action;
      const layers = state.layers.map((l) => {
        if (l.id !== id) return l;
        const existing = new Set(l.members.map((m) => m.segmentId));
        const fresh = added.filter((a) => !existing.has(a.segmentId));
        return { ...l, members: [...l.members, ...fresh] };
      });
      return { ...state, layers };
    }

    case "createGroup": {
      const memberIds = (action.ids || []).filter((id) =>
        state.layers.some((l) => l.id === id),
      );
      if (memberIds.length < 2) return state;
      // If any selected layer is already in a group, ignore those (keep simple).
      const eligible = memberIds.filter((id) => {
        const l = state.layers.find((x) => x.id === id);
        return l && !l.groupId;
      });
      if (eligible.length < 2) return state;

      const groupId = `grp_${nanoid(8)}`;
      const group = {
        id: groupId,
        name: action.name || `Group ${state.groups.length + 1}`,
        collapsed: false,
      };

      // Reorder layers so members form a contiguous block, ending where
      // the topmost (highest-z) member used to be.
      const memberSet = new Set(eligible);
      const members = state.layers.filter((l) => memberSet.has(l.id))
        .map((l) => ({ ...l, groupId }));
      const others = state.layers.filter((l) => !memberSet.has(l.id));

      // Where does the topmost selected layer live in the original array?
      let highestSelectedIdx = -1;
      state.layers.forEach((l, i) => { if (memberSet.has(l.id)) highestSelectedIdx = i; });
      // Translate that to an insertion index in `others`.
      let insertAt = 0;
      for (let i = 0; i <= highestSelectedIdx; i++) {
        if (!memberSet.has(state.layers[i].id)) insertAt++;
      }

      const reordered = [
        ...others.slice(0, insertAt),
        ...members,
        ...others.slice(insertAt),
      ];

      return {
        ...state,
        layers: reordered,
        groups: [...state.groups, group],
        selectedIds: eligible,
      };
    }

    case "ungroup": {
      const groupId = action.groupId;
      if (!groupId) return state;
      const layers = state.layers.map((l) =>
        l.groupId === groupId ? { ...l, groupId: null } : l,
      );
      const groups = state.groups.filter((g) => g.id !== groupId);
      return { ...state, layers, groups };
    }

    case "toggleGroupCollapse": {
      const groups = state.groups.map((g) =>
        g.id === action.groupId ? { ...g, collapsed: !g.collapsed } : g,
      );
      return { ...state, groups };
    }

    case "renameGroup": {
      const groups = state.groups.map((g) =>
        g.id === action.groupId ? { ...g, name: action.name } : g,
      );
      return { ...state, groups };
    }

    case "setCanvasSize": {
      const w = Math.max(1, Math.round(action.w));
      const h = Math.max(1, Math.round(action.h));
      if (state.canvasSize?.w === w && state.canvasSize?.h === h) return state;
      return { ...state, canvasSize: { w, h } };
    }

    default:
      return state;
  }
}

function dedupe(arr) {
  const seen = new Set();
  const out = [];
  for (const x of arr) {
    if (!seen.has(x)) { seen.add(x); out.push(x); }
  }
  return out;
}

// ---------------------------------------------------------------------------
// History (undo/redo) wrapper around the inner reducer.
// ---------------------------------------------------------------------------

const HISTORY_CAP = 50;

// Action types whose effects should land in the undo stack. Everything
// else (selection changes, group collapse toggling, viewport, etc.) just
// updates the present state without polluting the history.
const UNDOABLE_ACTIONS = new Set([
  "addLayer",
  "removeLayer",
  "removeLayers",
  "transformLayer",
  "transformLayers",
  "toggleMirror",
  "reorderLayers",
  "bringToFront",
  "sendToBack",
  "duplicateLayers",
  "duplicateLayersInPlace",
  "recomposeLayer",
  "createGroup",
  "ungroup",
  "renameGroup",
  "setLayerLocked",
  "setLayerHidden",
  "setCanvasSize",
]);

function withHistory(present) {
  return { past: [], present, future: [] };
}

function rootReducer(state, action) {
  if (action.type === "undo") {
    if (state.past.length === 0) return state;
    const previous = state.past[state.past.length - 1];
    return {
      past: state.past.slice(0, -1),
      present: previous,
      future: [state.present, ...state.future].slice(0, HISTORY_CAP),
    };
  }
  if (action.type === "redo") {
    if (state.future.length === 0) return state;
    const [next, ...rest] = state.future;
    return {
      past: [...state.past, state.present].slice(-HISTORY_CAP),
      present: next,
      future: rest,
    };
  }
  if (action.type === "hydrate") {
    return withHistory(sanitizePayload(action.payload));
  }
  const newPresent = reducer(state.present, action);
  if (newPresent === state.present) return state;
  if (UNDOABLE_ACTIONS.has(action.type)) {
    return {
      past: [...state.past, state.present].slice(-HISTORY_CAP),
      present: newPresent,
      future: [],
    };
  }
  return { ...state, present: newPresent };
}

export function useCanvas(canvasId) {
  const [wrapped, dispatch] = useReducer(rootReducer, undefined, () => withHistory(emptyState()));
  const state = wrapped.present;
  const canUndo = wrapped.past.length > 0;
  const canRedo = wrapped.future.length > 0;
  const lastSavedRef = useRef(null);
  const saveTimerRef = useRef(null);
  const skipNextSaveRef = useRef(false);
  const activeCanvasRef = useRef(canvasId);

  // Hydrate (or re-hydrate) when canvasId changes. Resets the history and
  // skips the next autosave so the freshly-loaded payload doesn't echo
  // back to the server.
  useEffect(() => {
    activeCanvasRef.current = canvasId;
    if (canvasId == null) return;
    let cancelled = false;
    fetchCanvasById(canvasId)
      .then((canvas) => {
        if (cancelled || activeCanvasRef.current !== canvasId) return;
        const payload = canvas?.payload || emptyState();
        skipNextSaveRef.current = true;
        dispatch({ type: "hydrate", payload });
        lastSavedRef.current = null;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canvasId]);

  // Debounced autosave — saves only the present state, not the history.
  useEffect(() => {
    if (canvasId == null) return;
    const serialized = JSON.stringify(state);
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (serialized === lastSavedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      putCanvasById(canvasId, state)
        .then(() => { lastSavedRef.current = serialized; })
        .catch((err) => { console.warn("autosave failed:", err.message); });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [state, canvasId]);

  const undo = useCallback(() => dispatch({ type: "undo" }), []);
  const redo = useCallback(() => dispatch({ type: "redo" }), []);

  const addLayer = useCallback((layer) => dispatch({ type: "addLayer", layer }), []);
  const selectLayers = useCallback((ids) => dispatch({ type: "selectLayers", ids }), []);
  const toggleLayerSelection = useCallback(
    (id) => dispatch({ type: "toggleLayerSelection", id }),
    [],
  );
  const clearSelection = useCallback(() => dispatch({ type: "selectLayers", ids: [] }), []);
  const removeLayer = useCallback((id) => dispatch({ type: "removeLayer", id }), []);
  const removeLayers = useCallback((ids) => dispatch({ type: "removeLayers", ids }), []);
  const transformLayer = useCallback(
    (id, patch) => dispatch({ type: "transformLayer", id, patch }),
    [],
  );
  const transformLayers = useCallback(
    (patches) => dispatch({ type: "transformLayers", patches }),
    [],
  );
  const toggleMirror = useCallback(
    (ids, axis) => dispatch({ type: "toggleMirror", ids, axis }),
    [],
  );
  const reorderLayers = useCallback(
    (fromIdx, toIdx) => dispatch({ type: "reorderLayers", fromIdx, toIdx }),
    [],
  );
  const recomposeLayer = useCallback(
    (id, added) => dispatch({ type: "recomposeLayer", id, added }),
    [],
  );
  const bringToFront = useCallback((ids) => dispatch({ type: "bringToFront", ids }), []);
  const sendToBack = useCallback((ids) => dispatch({ type: "sendToBack", ids }), []);
  const duplicateLayers = useCallback(
    (ids, offset) => dispatch({ type: "duplicateLayers", ids, offset }),
    [],
  );
  const duplicateLayersInPlace = useCallback(
    (ids) => dispatch({ type: "duplicateLayersInPlace", ids }),
    [],
  );
  const setLayerLocked = useCallback(
    (ids, locked) => dispatch({ type: "setLayerLocked", ids, locked }),
    [],
  );
  const setLayerHidden = useCallback(
    (ids, hidden) => dispatch({ type: "setLayerHidden", ids, hidden }),
    [],
  );
  const createGroup = useCallback(
    (ids, name) => dispatch({ type: "createGroup", ids, name }),
    [],
  );
  const ungroup = useCallback(
    (groupId) => dispatch({ type: "ungroup", groupId }),
    [],
  );
  const toggleGroupCollapse = useCallback(
    (groupId) => dispatch({ type: "toggleGroupCollapse", groupId }),
    [],
  );
  const renameGroup = useCallback(
    (groupId, name) => dispatch({ type: "renameGroup", groupId, name }),
    [],
  );
  const setCanvasSize = useCallback(
    (w, h) => dispatch({ type: "setCanvasSize", w, h }),
    [],
  );

  return {
    state,
    canUndo,
    canRedo,
    undo,
    redo,
    addLayer,
    selectLayers,
    toggleLayerSelection,
    clearSelection,
    removeLayer,
    removeLayers,
    transformLayer,
    transformLayers,
    toggleMirror,
    reorderLayers,
    bringToFront,
    sendToBack,
    duplicateLayers,
    duplicateLayersInPlace,
    setLayerLocked,
    setLayerHidden,
    recomposeLayer,
    createGroup,
    ungroup,
    toggleGroupCollapse,
    renameGroup,
    setCanvasSize,
  };
}

export function usedSegmentIds(state) {
  const ids = new Set();
  for (const l of state.layers || []) {
    if (!Array.isArray(l?.members)) continue;
    for (const m of l.members) if (m && Number.isFinite(m.segmentId)) ids.add(m.segmentId);
  }
  return Array.from(ids);
}

// Helper: the set of layer ids in a given group, in z-order (lowest first).
export function groupMemberIds(state, groupId) {
  return state.layers.filter((l) => l.groupId === groupId).map((l) => l.id);
}
